import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded'
import AudiotrackRoundedIcon from '@mui/icons-material/AudiotrackRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded'
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded'
import DoneRoundedIcon from '@mui/icons-material/DoneRounded'
import EmailRoundedIcon from '@mui/icons-material/EmailRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import HistoryEduRoundedIcon from '@mui/icons-material/HistoryEduRounded'
import ImageRoundedIcon from '@mui/icons-material/ImageRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import VideoFileRoundedIcon from '@mui/icons-material/VideoFileRounded'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Drawer,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { DISPATCH_EVENT_KEYS, EVENT_TYPES_ARRAY, SIGNALING_COMMANDS } from 'src/consumer/constants'
import { addWindowSignalingEventListener } from 'src/live'
import PortalVoiceCallButton from 'src/mentor/components/voice/PortalVoiceCallButton'
import { getPortalVoiceOptions } from 'src/mentor/services/portalVoiceCallService'
import {
  getSupportAgentContactCapabilities,
  getSupportAgentWhatsappThread,
  prepareSupportAgentEmail,
  sendSupportAgentWhatsappAttachment,
  sendSupportAgentWhatsappMessage,
  sendSupportAgentWhatsappTemplate,
} from 'src/supportPortal/services/supportAgentContactService'

const DEFAULT_EVENT_TYPE_OPTIONS = EVENT_TYPES_ARRAY.filter((option) => option?.id).map((option) => ({
  value: option.id,
  label: option.title || option.id,
}))

const supportPortalVoicePreviewCache = new Map()

function normalizeEventTypeOptions(options = []) {
  if (!Array.isArray(options) || options.length === 0) {
    return DEFAULT_EVENT_TYPE_OPTIONS
  }

  return options
    .map((option) => {
      if (typeof option === 'string') {
        return { value: option, label: option }
      }
      return {
        value: option?.value || option?.id || '',
        label: option?.label || option?.title || option?.value || option?.id || '',
      }
    })
    .filter((option) => option.value)
}

function buildSupportPortalVoicePreviewKey(context = {}) {
  return JSON.stringify({
    patientId: context?.patientId || '',
    practitionerId: context?.practitionerId || '',
    bookedEventId: context?.bookedEventId || '',
    clinicBrandId: context?.clinicBrandId || '',
    clinicId: context?.clinicId || '',
    targetRole: context?.targetRole || '',
    eventType: context?.eventType || '',
    eventTypesCsv: context?.eventTypesCsv || '',
  })
}

function pickSupportPortalVoicePreviewRoute(routes = [], preferredDestinationType = '') {
  if (!Array.isArray(routes) || routes.length === 0) {
    return null
  }

  const filteredRoutes = preferredDestinationType
    ? routes.filter((route) => (route?.destinationType || route?.id) === preferredDestinationType)
    : routes
  const candidateRoutes = filteredRoutes.length > 0 ? filteredRoutes : routes
  return candidateRoutes.find((route) => route?.enabled) || candidateRoutes[0] || null
}

function getProviderBadge(providerType = '') {
  if (providerType === 'plivo') {
    return '(P)'
  }
  if (providerType === 'twilio') {
    return '(T)'
  }
  return ''
}

function getProviderLabel(providerType = '', fallbackLabel = '') {
  if (fallbackLabel) {
    return fallbackLabel
  }
  if (providerType === 'plivo') {
    return 'Plivo'
  }
  if (providerType === 'twilio') {
    return 'Twilio'
  }
  return 'Provider'
}

function formatRouteCallerNumbers(callerNumbers = []) {
  if (!Array.isArray(callerNumbers) || callerNumbers.length === 0) {
    return ''
  }
  return callerNumbers.filter(Boolean).join(', ')
}

function formatSupportRouteProvider(candidate) {
  if (!candidate) {
    return ''
  }

  return [
    getProviderLabel(candidate?.providerType, candidate?.providerLabel),
    formatRouteCallerNumbers(candidate?.callerNumbers || []),
    getProviderBadge(candidate?.providerType),
  ]
    .filter(Boolean)
    .join(' ')
}

function parseThreadTimestamp(value) {
  if (!value) {
    return null
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }

  if (typeof value === 'number') {
    const numericDate = new Date(value < 1e12 ? value * 1000 : value)
    return Number.isNaN(numericDate.getTime()) ? null : numericDate
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) {
      return null
    }
    if (/^\d{10,13}$/.test(trimmed)) {
      const numericValue = Number(trimmed)
      const numericDate = new Date(trimmed.length <= 10 ? numericValue * 1000 : numericValue)
      return Number.isNaN(numericDate.getTime()) ? null : numericDate
    }
    const directDate = new Date(trimmed)
    if (!Number.isNaN(directDate.getTime())) {
      return directDate
    }
    const normalizedDate = new Date(trimmed.replace(' ', 'T'))
    return Number.isNaN(normalizedDate.getTime()) ? null : normalizedDate
  }

  if (typeof value === 'object') {
    if (value?.$date) {
      return parseThreadTimestamp(value.$date)
    }
    if (value?.epochSecond !== undefined) {
      const milliseconds = (Number(value.epochSecond) * 1000) + Math.floor(Number(value.nano || 0) / 1000000)
      return parseThreadTimestamp(milliseconds)
    }
    if (value?.seconds !== undefined) {
      const milliseconds = (Number(value.seconds) * 1000) + Math.floor(Number(value.nanos || value.nano || 0) / 1000000)
      return parseThreadTimestamp(milliseconds)
    }
    if (value?.time !== undefined) {
      return parseThreadTimestamp(value.time)
    }
    if (value?.value !== undefined) {
      return parseThreadTimestamp(value.value)
    }
  }

  return null
}

function formatThreadTimestamp(value) {
  const date = parseThreadTimestamp(value)
  if (!date) {
    return ''
  }

  const now = new Date()
  const isSameYear = now.getFullYear() === date.getFullYear()
  const isSameDay = now.getFullYear() === date.getFullYear()
    && now.getMonth() === date.getMonth()
    && now.getDate() === date.getDate()

  return new Intl.DateTimeFormat('en-IN', {
    ...(isSameDay ? {} : { day: 'numeric', month: 'short' }),
    ...(isSameYear || isSameDay ? {} : { year: 'numeric' }),
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)
}

function getTemplateActionChipColor(actionType) {
  if (actionType === 'call-heads-up') {
    return 'warning'
  }
  if (actionType === 'permission-template') {
    return 'info'
  }
  return 'default'
}

function getTemplateActionFriendlyLabel(actionType, fallbackLabel) {
  if (fallbackLabel) {
    return fallbackLabel
  }
  if (actionType === 'permission-template') {
    return 'Conversation opener'
  }
  if (actionType === 'call-heads-up') {
    return 'Call heads-up'
  }
  if (actionType === 'inbound-reply') {
    return 'Reply'
  }
  if (actionType === 'free-form') {
    return 'Message'
  }
  return 'Interaction'
}

function getDeliveryStatusMeta(status) {
  const normalized = `${status || ''}`.toLowerCase()
  if (normalized === 'read') {
    return { label: 'Read', color: '#2563eb', icon: <DoneAllRoundedIcon sx={{ fontSize: 16 }} /> }
  }
  if (normalized === 'delivered') {
    return { label: 'Delivered', color: '#0f766e', icon: <DoneAllRoundedIcon sx={{ fontSize: 16 }} /> }
  }
  if (normalized === 'sent' || normalized === 'queued' || normalized === 'accepted') {
    return {
      label: normalized === 'queued' ? 'Queued' : 'Sent',
      color: '#475569',
      icon: normalized === 'queued'
        ? <ScheduleRoundedIcon sx={{ fontSize: 15 }} />
        : <DoneRoundedIcon sx={{ fontSize: 16 }} />,
    }
  }
  if (normalized === 'failed' || normalized === 'undelivered') {
    return {
      label: normalized === 'undelivered' ? 'Undelivered' : 'Failed',
      color: '#dc2626',
      icon: <ErrorOutlineRoundedIcon sx={{ fontSize: 15 }} />,
    }
  }
  return {
    label: status || 'Sending',
    color: '#475569',
    icon: <ScheduleRoundedIcon sx={{ fontSize: 15 }} />,
  }
}

const WHATSAPP_ATTACHMENT_OPTIONS = [
  {
    id: 'media',
    label: 'Photos & videos',
    helper: 'Images and video clips',
    accept: 'image/*,video/*',
    kind: 'media',
  },
  {
    id: 'document',
    label: 'Document',
    helper: 'PDF, DOC, XLS and more',
    accept: '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx,.zip,.rar,.7z',
    kind: 'document',
  },
  {
    id: 'audio',
    label: 'Audio',
    helper: 'Voice notes and audio files',
    accept: 'audio/*',
    kind: 'audio',
  },
  {
    id: 'all',
    label: 'Any file',
    helper: 'Fallback for other attachments',
    accept: '*/*',
    kind: 'document',
  },
]

function getComposerAttachmentIcon(kind, size = 18) {
  if (kind === 'media' || kind === 'image') {
    return <ImageRoundedIcon sx={{ fontSize: size }} />
  }
  if (kind === 'video') {
    return <VideoFileRoundedIcon sx={{ fontSize: size }} />
  }
  if (kind === 'audio') {
    return <AudiotrackRoundedIcon sx={{ fontSize: size }} />
  }
  return <DescriptionRoundedIcon sx={{ fontSize: size }} />
}

function getPendingAttachmentKind(file) {
  const mimeType = `${file?.type || ''}`.toLowerCase()
  const fileName = `${file?.name || ''}`.toLowerCase()
  if (mimeType.startsWith('image/')) {
    return 'image'
  }
  if (mimeType.startsWith('video/')) {
    return 'video'
  }
  if (mimeType.startsWith('audio/')) {
    return 'audio'
  }
  if (/\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(fileName)) {
    return 'image'
  }
  if (/\.(mp4|mov|avi|mkv|webm)$/i.test(fileName)) {
    return 'video'
  }
  if (/\.(mp3|wav|aac|ogg|m4a)$/i.test(fileName)) {
    return 'audio'
  }
  return 'document'
}

function formatPendingAttachmentSize(sizeInBytes) {
  const size = Number(sizeInBytes || 0)
  if (!size) {
    return ''
  }
  if (size < 1024) {
    return `${size} B`
  }
  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(size < 10 * 1024 ? 1 : 0)} KB`
  }
  return `${(size / (1024 * 1024)).toFixed(size < 10 * 1024 * 1024 ? 1 : 0)} MB`
}

function describePendingAttachment(file) {
  const kind = getPendingAttachmentKind(file)
  const typeLabel = kind === 'image'
    ? 'Photo'
    : kind === 'video'
      ? 'Video'
      : kind === 'audio'
        ? 'Audio'
        : 'Document'
  const sizeLabel = formatPendingAttachmentSize(file?.size)
  return {
    kind,
    secondaryLabel: sizeLabel ? `${typeLabel} · ${sizeLabel}` : typeLabel,
  }
}

function buildThreadStateSignature(state) {
  const messages = (state?.messages || []).map((item) => [
    item?.id || '',
    item?.messageSid || '',
    item?.createdAt || '',
    item?.actionType || '',
    item?.status || '',
    item?.deliveryStatus || '',
    item?.messagePreview || '',
    item?.attachments?.length || 0,
  ])
  const templateActions = (state?.templateActions || []).map((item) => [item?.id || '', item?.label || ''])
  return JSON.stringify({
    messages,
    templateActions,
    freeFormAllowed: !!state?.freeFormAllowed,
    freeFormWindowStartAt: state?.freeFormWindowStartAt || '',
    freeFormWindowEndAt: state?.freeFormWindowEndAt || '',
    targetDisplayName: state?.targetDisplayName || '',
    targetPhoneNumber: state?.targetPhoneNumber || '',
    targetWhatsappNumber: state?.targetWhatsappNumber || '',
    targetEmail: state?.targetEmail || '',
    targetUserId: state?.targetUserId || '',
    targetActorId: state?.targetActorId || '',
    clinicBrandName: state?.clinicBrandName || '',
    sharedWhatsappPatientIds: state?.sharedWhatsappPatientIds || [],
  })
}

function getMessageIdentity(item) {
  if (!item) {
    return ''
  }
  return item?.id || item?.messageSid || ((item?.createdAt || '') + '::' + (item?.actionType || '') + '::' + (item?.direction || ''))
}

function sortThreadMessages(messages = []) {
  return [...messages].sort((left, right) => {
    const leftTime = parseThreadTimestamp(left?.createdAt)?.getTime() || 0
    const rightTime = parseThreadTimestamp(right?.createdAt)?.getTime() || 0
    if (leftTime !== rightTime) {
      return leftTime - rightTime
    }
    return getMessageIdentity(left).localeCompare(getMessageIdentity(right))
  })
}

function mergeThreadMessages(existingMessages = [], incomingMessage) {
  if (!incomingMessage) {
    return existingMessages || []
  }

  const nextMessages = [...(existingMessages || [])]
  const incomingIdentity = getMessageIdentity(incomingMessage)
  const existingIndex = nextMessages.findIndex((item) => {
    if (!item) {
      return false
    }
    if (incomingMessage?.messageSid && item?.messageSid && incomingMessage.messageSid === item.messageSid) {
      return true
    }
    return getMessageIdentity(item) === incomingIdentity
  })

  if (existingIndex >= 0) {
    nextMessages[existingIndex] = {
      ...nextMessages[existingIndex],
      ...incomingMessage,
      attachments: incomingMessage?.attachments || nextMessages[existingIndex]?.attachments || [],
    }
  } else {
    nextMessages.push(incomingMessage)
  }

  return sortThreadMessages(nextMessages)
}

function rolesMatchForThread(signalRole, currentRole) {
  if (!signalRole || !currentRole) {
    return true
  }
  if (signalRole === currentRole) {
    return true
  }
  return currentRole === 'practitioner' && ['doctor', 'therapist', 'screener', 'practitioner'].includes(signalRole)
}

function doesSignalMatchSupportThread(payload, { targetRole, targetActorId, targetUserId, bookedEventId }) {
  if (!payload) {
    return false
  }

  if (!rolesMatchForThread(payload?.targetRole, targetRole)) {
    return false
  }

  if (payload?.targetActorId && targetActorId && payload.targetActorId !== targetActorId) {
    return false
  }

  if (!payload?.targetActorId && payload?.targetUserId && targetUserId && payload.targetUserId !== targetUserId) {
    return false
  }

  if (
    !payload?.targetActorId &&
    !payload?.targetUserId &&
    payload?.bookedEventId &&
    bookedEventId &&
    payload.bookedEventId !== bookedEventId
  ) {
    return false
  }

  return true
}

function mergeSignalIntoThreadState(currentState, payload, fallbackDisplayName = '') {
  const safePayload = payload || {}
  const hasFreeFormAllowed = Object.prototype.hasOwnProperty.call(safePayload, 'freeFormAllowed')
  const hasSharedPatientIds = Object.prototype.hasOwnProperty.call(safePayload, 'sharedWhatsappPatientIds')

  return {
    ...currentState,
    messages: safePayload?.message
      ? mergeThreadMessages(currentState?.messages || [], safePayload.message)
      : (currentState?.messages || []),
    freeFormAllowed: hasFreeFormAllowed ? !!safePayload.freeFormAllowed : !!currentState?.freeFormAllowed,
    freeFormWindowStartAt:
      safePayload?.freeFormWindowStartAt !== undefined
        ? safePayload.freeFormWindowStartAt
        : (currentState?.freeFormWindowStartAt || null),
    freeFormWindowEndAt:
      safePayload?.freeFormWindowEndAt !== undefined
        ? safePayload.freeFormWindowEndAt
        : (currentState?.freeFormWindowEndAt || null),
    targetDisplayName: safePayload?.targetDisplayName || currentState?.targetDisplayName || fallbackDisplayName || '',
    targetPhoneNumber: safePayload?.targetPhoneNumber || currentState?.targetPhoneNumber || '',
    targetWhatsappNumber: safePayload?.targetWhatsappNumber || currentState?.targetWhatsappNumber || '',
    targetEmail: safePayload?.targetEmail || currentState?.targetEmail || '',
    targetUserId: safePayload?.targetUserId || currentState?.targetUserId || '',
    targetActorId: safePayload?.targetActorId || currentState?.targetActorId || '',
    clinicBrandName: safePayload?.clinicBrandName || currentState?.clinicBrandName || '',
    sharedWhatsappPatientIds: hasSharedPatientIds
      ? (safePayload.sharedWhatsappPatientIds || [])
      : (currentState?.sharedWhatsappPatientIds || []),
  }
}

function getAttachmentIcon(kind) {
  if (kind === 'image') {
    return <ImageRoundedIcon sx={{ fontSize: 18 }} />
  }
  if (kind === 'audio') {
    return <AudiotrackRoundedIcon sx={{ fontSize: 18 }} />
  }
  if (kind === 'video') {
    return <VideoFileRoundedIcon sx={{ fontSize: 18 }} />
  }
  return <DescriptionRoundedIcon sx={{ fontSize: 18 }} />
}

function normalizeAttachmentUrl(attachment) {
  return attachment?.previewUrl || attachment?.downloadUrl || attachment?.objectUrl || attachment?.url || ''
}

function getAttachmentKind(attachment) {
  if (attachment?.kind) {
    return attachment.kind
  }
  return getPendingAttachmentKind({
    type: attachment?.mimeType,
    name: attachment?.fileName,
  })
}

function buildAttachmentSummaryPreview(attachments = [], incoming = false) {
  if (!attachments.length) {
    return incoming ? 'Received a WhatsApp message' : 'Sent a WhatsApp message'
  }
  if (attachments.length === 1) {
    const fileName = attachments[0]?.fileName
    return `${incoming ? 'Received an attachment' : 'Sent an attachment'}${fileName ? `: ${fileName}` : ''}`
  }
  return `${incoming ? 'Received' : 'Sent'} ${attachments.length} attachments`
}

function splitTrailingUrlToken(token = '') {
  let cleanToken = `${token || ''}`
  let trailingText = ''
  while (/[),.;!?]$/.test(cleanToken)) {
    trailingText = cleanToken.slice(-1) + trailingText
    cleanToken = cleanToken.slice(0, -1)
  }
  return {
    cleanToken,
    trailingText,
  }
}

function normalizeExternalUrl(url = '') {
  if (!url) {
    return ''
  }
  return /^https?:\/\//i.test(url) ? url : `https://${url}`
}

function extractMessageUrls(text = '') {
  const matches = `${text || ''}`.match(/(?:https?:\/\/|www\.)[^\s<]+/gi) || []
  const seen = new Set()
  return matches
    .map((item) => splitTrailingUrlToken(item).cleanToken)
    .filter((item) => {
      if (!item || seen.has(item)) {
        return false
      }
      seen.add(item)
      return true
    })
}

function isImageLikeUrl(url = '') {
  return /\.(png|jpe?g|gif|webp|bmp|svg)(?:[?#].*)?$/i.test(url)
}

function AttachmentPreview({ attachment }) {
  const fileName = attachment?.fileName || 'Attachment'
  const url = normalizeAttachmentUrl(attachment)
  const kind = getAttachmentKind(attachment)

  if (!url) {
    return null
  }

  if (kind === 'image') {
    return (
      <Box
        component="a"
        href={url}
        target="_blank"
        rel="noreferrer"
        sx={{
          display: 'block',
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid rgba(148, 163, 184, 0.18)',
          textDecoration: 'none',
        }}
      >
        <Box
          component="img"
          src={url}
          alt={fileName}
          sx={{
            display: 'block',
            width: '100%',
            maxHeight: 240,
            objectFit: 'cover',
            background: '#e2e8f0',
          }}
        />
      </Box>
    )
  }

  if (kind === 'audio') {
    return (
      <Box sx={{ borderRadius: 2, overflow: 'hidden', background: 'rgba(255,255,255,0.7)' }}>
        <audio controls preload="metadata" src={url} style={{ width: '100%' }} />
      </Box>
    )
  }

  if (kind === 'video') {
    return (
      <Box sx={{ borderRadius: 2, overflow: 'hidden', background: '#0f172a' }}>
        <video controls preload="metadata" src={url} style={{ width: '100%', maxHeight: 260 }} />
      </Box>
    )
  }

  return (
    <Button
      component="a"
      href={url}
      target="_blank"
      rel="noreferrer"
      variant="outlined"
      size="small"
      startIcon={getAttachmentIcon(kind)}
      endIcon={<OpenInNewRoundedIcon />}
      sx={{
        alignSelf: 'flex-start',
        justifyContent: 'flex-start',
        borderRadius: 2,
        textTransform: 'none',
      }}
    >
      {fileName}
    </Button>
  )
}

function LinkPreviewCard({ url }) {
  const href = normalizeExternalUrl(url)
  if (!href) {
    return null
  }

  let hostname = href
  let secondaryText = href
  try {
    const parsed = new URL(href)
    hostname = parsed.hostname.replace(/^www\./i, '')
    secondaryText = `${parsed.pathname || ''}${parsed.search || ''}${parsed.hash || ''}` || parsed.hostname
  } catch (error) {
    // Keep the raw URL as fallback.
  }

  if (isImageLikeUrl(href)) {
    return (
      <Box
        component="a"
        href={href}
        target="_blank"
        rel="noreferrer"
        sx={{
          display: 'block',
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid rgba(148, 163, 184, 0.18)',
          textDecoration: 'none',
        }}
      >
        <Box
          component="img"
          src={href}
          alt={hostname}
          sx={{
            display: 'block',
            width: '100%',
            maxHeight: 220,
            objectFit: 'cover',
            background: '#e2e8f0',
          }}
        />
      </Box>
    )
  }

  return (
    <Paper
      component="a"
      href={href}
      target="_blank"
      rel="noreferrer"
      variant="outlined"
      sx={{
        px: 1.2,
        py: 1,
        borderRadius: 2,
        display: 'block',
        textDecoration: 'none',
        color: 'inherit',
        borderColor: 'rgba(148, 163, 184, 0.22)',
        background: 'rgba(255, 255, 255, 0.7)',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 1.75,
            display: 'grid',
            placeItems: 'center',
            background: 'rgba(226, 232, 240, 0.9)',
            color: '#0f172a',
            flexShrink: 0,
          }}
        >
          <OpenInNewRoundedIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: '#0f172a' }} noWrap>
            {hostname}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {secondaryText || href}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  )
}

function renderWhatsappMarkup(text) {
  if (!text) {
    return null
  }

  const segments = []
  const regex = /(```[\s\S]+?```|\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~|(?:https?:\/\/|www\.)[^\s<]+)/g
  let lastIndex = 0
  let match
  let keyIndex = 0

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push(
        <Fragment key={`plain-${keyIndex}`}>
          {text.slice(lastIndex, match.index)}
        </Fragment>,
      )
      keyIndex += 1
    }

    const token = match[0]
    const wrapperKey = `token-${keyIndex}`
    if (token.startsWith('```')) {
      segments.push(
        <Box
          component="span"
          key={wrapperKey}
          sx={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace',
            background: 'rgba(148, 163, 184, 0.2)',
            borderRadius: 1,
            px: 0.6,
            py: 0.2,
            whiteSpace: 'pre-wrap',
            display: 'inline-block',
          }}
        >
          {token.slice(3, -3)}
        </Box>,
      )
    } else if (token.startsWith('*')) {
      segments.push(
        <Box component="strong" key={wrapperKey} sx={{ fontWeight: 700 }}>
          {token.slice(1, -1)}
        </Box>,
      )
    } else if (token.startsWith('_')) {
      segments.push(
        <Box component="em" key={wrapperKey} sx={{ fontStyle: 'italic' }}>
          {token.slice(1, -1)}
        </Box>,
      )
    } else if (token.startsWith('~')) {
      segments.push(
        <Box component="span" key={wrapperKey} sx={{ textDecoration: 'line-through' }}>
          {token.slice(1, -1)}
        </Box>,
      )
    } else {
      const { cleanToken, trailingText } = splitTrailingUrlToken(token)
      const href = normalizeExternalUrl(cleanToken)
      segments.push(
        <Box
          component="a"
          key={wrapperKey}
          href={href}
          target="_blank"
          rel="noreferrer"
          sx={{
            color: '#2563eb',
            textDecoration: 'underline',
            wordBreak: 'break-all',
          }}
        >
          {cleanToken}
        </Box>,
      )
      if (trailingText) {
        segments.push(
          <Fragment key={`${wrapperKey}-trail`}>
            {trailingText}
          </Fragment>,
        )
      }
    }
    keyIndex += 1
    lastIndex = match.index + token.length
  }

  if (lastIndex < text.length) {
    segments.push(
      <Fragment key={`plain-${keyIndex}`}>
        {text.slice(lastIndex)}
      </Fragment>,
    )
  }

  return segments
}

function mergeServerMessagesWithLocal(serverMessages = [], currentMessages = []) {
  const normalizedServerMessages = sortThreadMessages(serverMessages || [])
  const unresolvedLocalMessages = (currentMessages || []).filter((item) => {
    if (!item?.localEcho) {
      return false
    }
    return !normalizedServerMessages.some((serverItem) => {
      if (item?.messageSid && serverItem?.messageSid && item.messageSid === serverItem.messageSid) {
        return true
      }
      return getMessageIdentity(serverItem) === getMessageIdentity(item)
    })
  })
  return sortThreadMessages([...normalizedServerMessages, ...unresolvedLocalMessages])
}

function patchThreadMessages(messages = [], messageId, buildNextMessage) {
  return sortThreadMessages((messages || []).map((item) => {
    if ((item?.id || '') !== messageId) {
      return item
    }
    return typeof buildNextMessage === 'function'
      ? buildNextMessage(item || {})
      : { ...(item || {}), ...(buildNextMessage || {}) }
  }))
}

function buildLocalMessagePreview(messageText, attachments = []) {
  if (messageText) {
    return messageText
  }
  return buildAttachmentSummaryPreview(attachments, false)
}

function buildOptimisticAttachments(files = []) {
  return (files || []).map((file, index) => {
    const objectUrl = URL.createObjectURL(file)
    return {
      fileId: `local-file-${Date.now()}-${index}`,
      fileName: file?.name || `Attachment ${index + 1}`,
      mimeType: file?.type || '',
      fileSize: file?.size || 0,
      kind: getPendingAttachmentKind(file),
      previewUrl: objectUrl,
      downloadUrl: objectUrl,
      objectUrl,
      isLocalPreview: true,
    }
  })
}

function buildOptimisticMessage({ messageId, messageText, attachmentFiles, targetDisplayName, supportAgentName = 'Support' }) {
  const attachments = buildOptimisticAttachments(attachmentFiles)
  return {
    id: messageId,
    localEcho: true,
    retryable: false,
    direction: 'outgoing',
    actionType: 'free-form',
    label: 'Message',
    messageType: attachments.length > 0 ? 'attachment' : 'message',
    status: 'sending',
    deliveryStatus: 'sending',
    messagePreview: buildLocalMessagePreview(messageText, attachments),
    supportAgentName,
    targetDisplayName: targetDisplayName || '',
    createdAt: new Date().toISOString(),
    metadata: {
      rawMessage: messageText || '',
    },
    attachments,
    attachmentCount: attachments.length,
  }
}

function MessageBubble({ item, onRetry }) {
  const incoming = item?.direction === 'incoming'
  const attachments = item?.attachments || []
  const deliveryMeta = !incoming ? getDeliveryStatusMeta(item?.deliveryStatus || item?.status) : null
  const label = getTemplateActionFriendlyLabel(item?.actionType, item?.label)
  const rawMessage = `${item?.metadata?.rawMessage || ''}`.trim()
  const fallbackPreview = item?.messagePreview || 'No message preview was captured for this interaction.'
  const shouldRenderText = Boolean(rawMessage || attachments.length === 0)
  const messageText = rawMessage || (attachments.length === 0 ? fallbackPreview : '')
  const linkPreviews = extractMessageUrls(rawMessage || (attachments.length === 0 ? fallbackPreview : '')).slice(0, 2)
  const canRetry = Boolean(!incoming && item?.retryable && typeof onRetry === 'function')

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: incoming ? 'flex-start' : 'flex-end',
      }}
    >
      <Box
        sx={{
          maxWidth: '86%',
          px: 1.5,
          py: 1.2,
          borderRadius: 2.5,
          background: incoming
            ? 'rgba(241, 245, 249, 0.96)'
            : 'linear-gradient(135deg, rgba(220, 252, 231, 0.98) 0%, rgba(209, 250, 229, 0.95) 100%)',
          border: incoming
            ? '1px solid rgba(148, 163, 184, 0.18)'
            : '1px solid rgba(22, 163, 74, 0.18)',
          boxShadow: incoming
            ? '0 8px 24px rgba(15, 23, 42, 0.05)'
            : '0 10px 26px rgba(22, 163, 74, 0.08)',
        }}
      >
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 0.75, flexWrap: 'wrap' }}
        >
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#0f172a' }}>
              {incoming ? item?.targetDisplayName || 'Recipient' : item?.supportAgentName || 'Support'}
            </Typography>
            <Chip
              size="small"
              label={label}
              color={getTemplateActionChipColor(item?.actionType)}
              variant="outlined"
              sx={{ height: 22 }}
            />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {formatThreadTimestamp(item?.createdAt)}
          </Typography>
        </Stack>

        {shouldRenderText ? (
          <Typography
            variant="body2"
            component="div"
            sx={{
              color: '#111827',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              lineHeight: 1.55,
            }}
          >
            {renderWhatsappMarkup(messageText)}
          </Typography>
        ) : null}

        {attachments.length > 0 ? (
          <Stack spacing={1} sx={{ mt: shouldRenderText ? 1 : 0.25 }}>
            {attachments.map((attachment, index) => (
              <AttachmentPreview
                key={`${attachment?.fileId || attachment?.downloadUrl || 'attachment'}-${index}`}
                attachment={attachment}
              />
            ))}
          </Stack>
        ) : null}

        {linkPreviews.length > 0 ? (
          <Stack spacing={0.8} sx={{ mt: shouldRenderText || attachments.length > 0 ? 1 : 0.25 }}>
            {linkPreviews.map((url) => (
              <LinkPreviewCard key={url} url={url} />
            ))}
          </Stack>
        ) : null}

        <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="flex-end" sx={{ mt: 1, flexWrap: 'wrap' }}>
          {attachments.length > 0 ? (
            <Chip
              size="small"
              icon={<AttachFileRoundedIcon sx={{ fontSize: 14 }} />}
              label={`${attachments.length} attachment${attachments.length === 1 ? '' : 's'}`}
              variant="outlined"
              sx={{ height: 22 }}
            />
          ) : null}
          {deliveryMeta ? (
            <Stack direction="row" spacing={0.4} alignItems="center" sx={{ color: deliveryMeta.color }}>
              {deliveryMeta.icon}
              <Typography variant="caption" sx={{ color: deliveryMeta.color, fontWeight: 700 }}>
                {deliveryMeta.label}
              </Typography>
            </Stack>
          ) : null}
          {canRetry ? (
            <Button
              size="small"
              variant="text"
              onClick={() => onRetry(item)}
              startIcon={<RefreshRoundedIcon sx={{ fontSize: 15 }} />}
              sx={{ minWidth: 0, px: 0.5, textTransform: 'none' }}
            >
              Retry
            </Button>
          ) : null}
        </Stack>

        {!incoming && item?.deliveryErrorMessage ? (
          <Typography variant="caption" sx={{ display: 'block', mt: 0.6, color: '#dc2626' }}>
            {item.deliveryErrorMessage}
          </Typography>
        ) : null}
      </Box>
    </Box>
  )
}

export default function SupportContactActions({
  patientId,
  practitionerId,
  bookedEventId,
  clinicBrandId,
  clinicBrandName,
  clinicId,
  targetRole,
  eventType,
  eventTypesCsv,
  eventTypeOptions,
  uiSurface = 'support-contact-actions',
  title = 'Contact Actions',
  compact = false,
  compactIconStrip = false,
  lazyUntilExpand = false,
  sx = {},
}) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const normalizedEventTypeOptions = useMemo(
    () => normalizeEventTypeOptions(eventTypeOptions),
    [eventTypeOptions],
  )
  const [selectedEventType, setSelectedEventType] = useState(
    eventType || normalizedEventTypeOptions[0]?.value || '',
  )
  const [loading, setLoading] = useState(false)
  const [capabilities, setCapabilities] = useState(null)
  const [capabilitiesError, setCapabilitiesError] = useState('')
  // When lazyUntilExpand is set (e.g. the clinic patients drawer), the panel starts collapsed and
  // does NOT load contact capabilities/phone-route until the user expands it. Defaults to expanded
  // everywhere else, so all other surfaces keep their current behavior exactly.
  const [expanded, setExpanded] = useState(!lazyUntilExpand)
  const [phoneRoutePreview, setPhoneRoutePreview] = useState({
    loading: false,
    loaded: false,
    route: null,
    error: '',
  })
  const [whatsappDrawerOpen, setWhatsappDrawerOpen] = useState(false)
  const [threadLoading, setThreadLoading] = useState(false)
  const [threadError, setThreadError] = useState('')
  const [threadState, setThreadState] = useState({
    messages: [],
    templateActions: [],
    freeFormAllowed: false,
    freeFormWindowStartAt: null,
    freeFormWindowEndAt: null,
    targetDisplayName: '',
    targetPhoneNumber: '',
    targetWhatsappNumber: '',
    targetEmail: '',
    targetUserId: '',
    targetActorId: '',
    clinicBrandName: '',
    sharedWhatsappPatientIds: [],
  })
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [selectedTemplateAction, setSelectedTemplateAction] = useState('')
  const [messageDraft, setMessageDraft] = useState('')
  const [backgroundSendCount, setBackgroundSendCount] = useState(0)
  const [templateSending, setTemplateSending] = useState(false)
  const [emailLoading, setEmailLoading] = useState(false)
  const [attachmentFiles, setAttachmentFiles] = useState([])
  const [attachmentInputKey, setAttachmentInputKey] = useState(0)
  const [attachmentMenuAnchorEl, setAttachmentMenuAnchorEl] = useState(null)
  const [attachmentPickerAccept, setAttachmentPickerAccept] = useState('*/*')
  const [attachmentPickerMode, setAttachmentPickerMode] = useState('all')
  const [showTemplatePanel, setShowTemplatePanel] = useState(true)
  const messagesEndRef = useRef(null)
  const attachmentInputRef = useRef(null)
  const lastChatActivityRef = useRef(null)
  const lastMessageSignatureRef = useRef('')
  const threadStateSignatureRef = useRef('')
  const currentMessagesRef = useRef([])
  const signalRefreshTimeoutRef = useRef(null)
  const retryPayloadsRef = useRef({})

  useEffect(() => {
    if (eventType) {
      setSelectedEventType(eventType)
      return
    }
    if (!selectedEventType && normalizedEventTypeOptions.length === 1) {
      setSelectedEventType(normalizedEventTypeOptions[0].value)
    }
  }, [eventType, normalizedEventTypeOptions, selectedEventType])

  const resolvedEventType = eventType || selectedEventType || ''
  const requiresEventTypeSelection = !eventTypesCsv && !resolvedEventType

  const context = useMemo(
    () => ({
      patientId,
      practitionerId,
      bookedEventId,
      clinicBrandId,
      clinicBrandName,
      clinicId,
      targetRole,
      eventType: resolvedEventType || undefined,
      eventTypesCsv: eventTypesCsv || undefined,
    }),
    [
      bookedEventId,
      clinicBrandId,
      clinicBrandName,
      clinicId,
      eventTypesCsv,
      patientId,
      practitionerId,
      resolvedEventType,
      targetRole,
    ],
  )

  // In lazy mode, collapse again whenever the target changes so each new patient starts un-loaded.
  useEffect(() => {
    if (lazyUntilExpand) {
      setExpanded(false)
    }
  }, [lazyUntilExpand, patientId, practitionerId])

  useEffect(() => {
    let active = true

    async function loadCapabilities() {
      if (lazyUntilExpand && !expanded) {
        return
      }
      if (!patientId && !practitionerId) {
        setCapabilities(null)
        setCapabilitiesError('')
        return
      }
      if (requiresEventTypeSelection) {
        setCapabilities(null)
        setCapabilitiesError('')
        return
      }

      setLoading(true)
      const result = await getSupportAgentContactCapabilities(context)
      if (!active) {
        return
      }

      if (result?.ok) {
        setCapabilities(result)
        setCapabilitiesError('')
      } else {
        setCapabilities(null)
        setCapabilitiesError(result?.message || 'Unable to load contact capabilities.')
      }
      setLoading(false)
    }

    loadCapabilities()

    return () => {
      active = false
    }
  }, [context, patientId, practitionerId, requiresEventTypeSelection, expanded, lazyUntilExpand])

  async function refreshCapabilities() {
    if (!patientId && !practitionerId) {
      return null
    }
    // Bypass the shared TTL cache — refreshes run right after sends, when the server-side
    // warm-window/capability state has just changed.
    const result = await getSupportAgentContactCapabilities(context, { forceRefresh: true })
    if (result?.ok) {
      setCapabilities(result)
      setCapabilitiesError("")
      return result
    }
    setCapabilities(null)
    setCapabilitiesError(result?.message || "Unable to load contact capabilities.")
    return result
  }

  useEffect(() => {
    let active = true
    const phoneChannel = capabilities?.channels?.phone || {}
    const hasSupportTarget = !!(patientId || practitionerId)
    const hasCallContext = !!(bookedEventId || (clinicBrandId && clinicId) || hasSupportTarget)
    const shouldResolvePhoneRoute =
      hasSupportTarget &&
      hasCallContext &&
      !requiresEventTypeSelection &&
      !!phoneChannel?.enabled

    if (!shouldResolvePhoneRoute) {
      setPhoneRoutePreview((currentValue) => {
        if (
          !currentValue?.loading &&
          !currentValue?.loaded &&
          !currentValue?.route &&
          !currentValue?.error
        ) {
          return currentValue
        }
        return {
          loading: false,
          loaded: false,
          route: null,
          error: '',
        }
      })
      return () => {
        active = false
      }
    }

    const previewKey = buildSupportPortalVoicePreviewKey(context)
    const cachedRoute = supportPortalVoicePreviewCache.get(previewKey)
    if (cachedRoute) {
      setPhoneRoutePreview({
        loading: false,
        loaded: true,
        route: cachedRoute,
        error: '',
      })
      return () => {
        active = false
      }
    }

    setPhoneRoutePreview((currentValue) => ({
      ...currentValue,
      loading: true,
      error: '',
    }))

    ;(async () => {
      const result = await getPortalVoiceOptions({
        actorRole: 'support-agent',
        bookedEventId,
        clinicBrandId,
        clinicId,
        practitionerId,
        patientId,
        targetRole,
        eventType: resolvedEventType || undefined,
        eventTypesCsv: eventTypesCsv || undefined,
      })

      if (!active) {
        return
      }

      const resolvedRoute = pickSupportPortalVoicePreviewRoute(
        result?.routes || [],
        phoneChannel?.destinationType,
      )

      if (resolvedRoute) {
        supportPortalVoicePreviewCache.set(previewKey, resolvedRoute)
      }

      setPhoneRoutePreview({
        loading: false,
        loaded: true,
        route: resolvedRoute,
        error: result?.ok ? '' : result?.message || 'Unable to load call route preview.',
      })
    })()

    return () => {
      active = false
    }
  }, [
    bookedEventId,
    capabilities?.channels?.phone?.destinationType,
    capabilities?.channels?.phone?.enabled,
    clinicBrandId,
    clinicId,
    context,
    eventTypesCsv,
    patientId,
    practitionerId,
    requiresEventTypeSelection,
    resolvedEventType,
    targetRole,
  ])

  function resetAttachmentPicker() {
    setAttachmentFiles([])
    setAttachmentInputKey((value) => value + 1)
  }

  function handleAttachmentSelection(event) {
    const nextFiles = Array.from(event?.target?.files || [])
    if (event?.target) {
      event.target.value = ''
    }
    if (nextFiles.length === 0) {
      return
    }
    setAttachmentFiles((currentValue) => [...currentValue, ...nextFiles])
  }

  function removeAttachmentAt(indexToRemove) {
    setAttachmentFiles((currentValue) => currentValue.filter((_, index) => index !== indexToRemove))
  }

  function openAttachmentMenu(event) {
    setAttachmentMenuAnchorEl(event?.currentTarget || null)
  }

  function closeAttachmentMenu() {
    setAttachmentMenuAnchorEl(null)
  }

  function triggerAttachmentPicker(option) {
    if (!option) {
      return
    }
    setAttachmentPickerAccept(option.accept || '*/*')
    setAttachmentPickerMode(option.id || 'all')
    setAttachmentMenuAnchorEl(null)
    window.setTimeout(() => {
      attachmentInputRef.current?.click()
    }, 40)
  }

  function applyThreadStateUpdate(nextValueOrUpdater) {
    setThreadState((currentValue) => {
      const nextValue = typeof nextValueOrUpdater === 'function'
        ? nextValueOrUpdater(currentValue)
        : nextValueOrUpdater
      currentMessagesRef.current = nextValue?.messages || []
      threadStateSignatureRef.current = buildThreadStateSignature(nextValue)
      return nextValue
    })
  }

  function handleComposerKeyDown(event) {
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent?.isComposing) {
      return
    }
    event.preventDefault()
    const hasMessage = !!messageDraft.trim()
    const canSendMessage = !!threadState?.freeFormAllowed && (hasMessage || attachmentFiles.length > 0)
    if (canSendMessage) {
      handleSendMessage()
    }
  }

  const loadWhatsappThread = useCallback(async (options = {}) => {
    const background = !!options?.background
    if (!background) {
      setThreadLoading(true)
    }
    setThreadError('')
    const result = await getSupportAgentWhatsappThread(context)
    if (result?.ok) {
      const messages = mergeServerMessagesWithLocal(result?.messages || [], currentMessagesRef.current)
      const nextThreadState = {
        messages,
        templateActions: result?.templateActions || [],
        freeFormAllowed: !!result?.freeFormAllowed,
        freeFormWindowStartAt: result?.freeFormWindowStartAt || null,
        freeFormWindowEndAt: result?.freeFormWindowEndAt || null,
        targetDisplayName: result?.targetDisplayName || capabilities?.targetDisplayName || '',
        targetPhoneNumber: result?.targetPhoneNumber || capabilities?.targetPhoneNumber || '',
        targetWhatsappNumber: result?.targetWhatsappNumber || capabilities?.targetWhatsappNumber || '',
        targetEmail: result?.targetEmail || capabilities?.targetEmail || '',
        targetUserId: result?.targetUserId || capabilities?.targetUserId || '',
        targetActorId: result?.targetActorId || capabilities?.targetActorId || '',
        clinicBrandName: result?.clinicBrandName || capabilities?.clinicBrandName || clinicBrandName || '',
        sharedWhatsappPatientIds: result?.sharedWhatsappPatientIds || capabilities?.sharedWhatsappPatientIds || [],
      }
      const nextThreadSignature = buildThreadStateSignature(nextThreadState)
      if (nextThreadSignature !== threadStateSignatureRef.current) {
        applyThreadStateUpdate(nextThreadState)
      }
      if (result?.freeFormAllowed) {
        setShowTemplatePanel(false)
      }
      setSelectedTemplateAction((currentValue) => {
        const availableTemplateIds = (result?.templateActions || []).map((item) => item?.id)
        if (currentValue && availableTemplateIds.includes(currentValue)) {
          return currentValue
        }
        return result?.templateActions?.[0]?.id || ''
      })
      const lastMessage = messages[messages.length - 1]
      const signature = `${messages.length}::${lastMessage?.id || lastMessage?.messageSid || lastMessage?.createdAt || ''}`
      if (signature && signature !== lastMessageSignatureRef.current) {
        lastMessageSignatureRef.current = signature
        lastChatActivityRef.current = Date.now()
      }
    } else {
      applyThreadStateUpdate({
        messages: [],
        templateActions: [],
        freeFormAllowed: false,
        freeFormWindowStartAt: null,
        freeFormWindowEndAt: null,
        targetDisplayName: capabilities?.targetDisplayName || '',
        targetPhoneNumber: capabilities?.targetPhoneNumber || '',
        targetWhatsappNumber: capabilities?.targetWhatsappNumber || '',
        targetEmail: capabilities?.targetEmail || '',
        targetUserId: capabilities?.targetUserId || '',
        targetActorId: capabilities?.targetActorId || '',
        clinicBrandName: capabilities?.clinicBrandName || clinicBrandName || '',
        sharedWhatsappPatientIds: capabilities?.sharedWhatsappPatientIds || [],
      })
      setShowTemplatePanel(true)
      setThreadError(result?.message || 'Unable to load the WhatsApp conversation.')
    }
    if (!background) {
      setThreadLoading(false)
    }
    return result
  }, [
    capabilities?.clinicBrandName,
    capabilities?.sharedWhatsappPatientIds,
    capabilities?.targetActorId,
    capabilities?.targetDisplayName,
    capabilities?.targetEmail,
    capabilities?.targetPhoneNumber,
    capabilities?.targetUserId,
    capabilities?.targetWhatsappNumber,
    clinicBrandName,
    context,
  ])

  async function openWhatsappDrawer() {
    setWhatsappDrawerOpen(true)
    lastChatActivityRef.current = Date.now()
    await loadWhatsappThread()
  }

  useEffect(() => {
    if (!whatsappDrawerOpen) {
      if (signalRefreshTimeoutRef.current) {
        window.clearTimeout(signalRefreshTimeoutRef.current)
        signalRefreshTimeoutRef.current = null
      }
      return undefined
    }

    const removeListener = addWindowSignalingEventListener({
      dispatchEventKey: DISPATCH_EVENT_KEYS.supportSignalingEvent,
      handler: (event) => {
        const detail = event?.detail
        if (detail?.command !== SIGNALING_COMMANDS.SUPPORT_WHATSAPP_THREAD_UPDATED) {
          return
        }

        const payload = detail?.payload || {}
        const currentTargetActorId = threadState?.targetActorId || capabilities?.targetActorId || patientId || practitionerId || ''
        const currentTargetUserId = threadState?.targetUserId || capabilities?.targetUserId || ''
        const matchesCurrentThread = doesSignalMatchSupportThread(payload, {
          targetRole: targetRole || threadState?.targetRole || capabilities?.targetRole,
          targetActorId: currentTargetActorId,
          targetUserId: currentTargetUserId,
          bookedEventId,
        })

        lastChatActivityRef.current = Date.now()

        if (matchesCurrentThread && (payload?.message || Object.prototype.hasOwnProperty.call(payload, 'freeFormAllowed'))) {
          setThreadError('')
          applyThreadStateUpdate((currentValue) => mergeSignalIntoThreadState(
            currentValue,
            payload,
            capabilities?.targetDisplayName,
          ))
          if (payload?.freeFormAllowed) {
            setShowTemplatePanel(false)
          }
        }

        if (signalRefreshTimeoutRef.current) {
          window.clearTimeout(signalRefreshTimeoutRef.current)
        }
        const refreshDelay = matchesCurrentThread && Boolean(payload?.message) ? 500 : 900
        signalRefreshTimeoutRef.current = window.setTimeout(() => {
          loadWhatsappThread({ background: true })
          signalRefreshTimeoutRef.current = null
        }, refreshDelay)
      },
    })

    return () => {
      if (signalRefreshTimeoutRef.current) {
        window.clearTimeout(signalRefreshTimeoutRef.current)
        signalRefreshTimeoutRef.current = null
      }
      if (typeof removeListener === 'function') {
        removeListener()
      }
    }
  }, [
    bookedEventId,
    capabilities?.targetActorId,
    capabilities?.targetDisplayName,
    capabilities?.targetRole,
    capabilities?.targetUserId,
    loadWhatsappThread,
    patientId,
    practitionerId,
    targetRole,
    threadState?.targetActorId,
    threadState?.targetRole,
    threadState?.targetUserId,
    whatsappDrawerOpen,
  ])

  useEffect(() => {
    if (!whatsappDrawerOpen) {
      lastChatActivityRef.current = null
      return undefined
    }
    if (!lastChatActivityRef.current) {
      lastChatActivityRef.current = Date.now()
    }
    return undefined
  }, [whatsappDrawerOpen])

  useEffect(() => {
    if (!whatsappDrawerOpen) {
      return undefined
    }

    let timeoutId

    const scheduleNext = () => {
      const elapsedMs = Date.now() - (lastChatActivityRef.current || Date.now())
let nextInterval = 60000
if (elapsedMs < 20 * 1000) {
  nextInterval = 4000
} else if (elapsedMs < 2 * 60 * 1000) {
  nextInterval = 10000
} else if (elapsedMs < 8 * 60 * 1000) {
  nextInterval = 20000
} else if (elapsedMs < 20 * 60 * 1000) {
  nextInterval = 45000
}

      if (document.hidden) {
        timeoutId = window.setTimeout(scheduleNext, Math.max(nextInterval, 60000))
        return
      }

      loadWhatsappThread({ background: true }).finally(() => {
        timeoutId = window.setTimeout(scheduleNext, nextInterval)
      })
    }

    timeoutId = window.setTimeout(scheduleNext, 1000)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [whatsappDrawerOpen, loadWhatsappThread])

  useEffect(() => {
    if (!whatsappDrawerOpen) {
      return undefined
    }
    const timeoutId = window.setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
    }, 80)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [threadState?.messages?.length, whatsappDrawerOpen])

  async function handleSendTemplate() {
    if (!selectedTemplateAction) {
      toast.error('Choose a template action first.')
      return
    }

    setTemplateSending(true)
    const result = await sendSupportAgentWhatsappTemplate(context, selectedTemplateAction)
    setTemplateSending(false)

    if (!result?.ok) {
      toast.error(result?.message || 'Unable to send the WhatsApp template.')
      return
    }

    toast.success('Template sent on WhatsApp.')
    await Promise.all([refreshCapabilities(), loadWhatsappThread({ background: true })])
    lastChatActivityRef.current = Date.now()
  }

  function updateLocalThreadMessage(localMessageId, buildNextMessage) {
    applyThreadStateUpdate((currentValue) => ({
      ...currentValue,
      messages: patchThreadMessages(currentValue?.messages || [], localMessageId, buildNextMessage),
    }))
  }

  async function sendMessageInBackground(localMessageId, payload) {
    const normalizedMessage = payload?.message?.trim() || ''
    const files = payload?.files || []
    setBackgroundSendCount((currentValue) => currentValue + 1)

    try {
      const result = files.length > 0
        ? await sendSupportAgentWhatsappAttachment(context, normalizedMessage, files)
        : await sendSupportAgentWhatsappMessage(context, normalizedMessage)

      if (!result?.ok) {
        updateLocalThreadMessage(localMessageId, (currentValue) => ({
          ...currentValue,
          status: 'failed',
          deliveryStatus: 'failed',
          retryable: true,
          deliveryErrorMessage: result?.message || 'Unable to send the WhatsApp message.',
        }))
        toast.error(result?.message || 'Unable to send the WhatsApp message.')
        return
      }

      delete retryPayloadsRef.current[localMessageId]
      updateLocalThreadMessage(localMessageId, (currentValue) => ({
        ...currentValue,
        messageSid: result?.data?.messageSid || currentValue?.messageSid,
        status: 'sent',
        deliveryStatus: 'sent',
        retryable: false,
        deliveryErrorMessage: '',
        attachments: result?.data?.attachments?.length ? result.data.attachments : (currentValue?.attachments || []),
        attachmentCount: result?.data?.attachments?.length ?? currentValue?.attachmentCount ?? 0,
      }))
      lastChatActivityRef.current = Date.now()
      await Promise.all([refreshCapabilities(), loadWhatsappThread({ background: true })]).catch(() => null)
    } catch (error) {
      const failureMessage = error?.response?.data?.message || error?.message || 'Unable to send the WhatsApp message.'
      updateLocalThreadMessage(localMessageId, (currentValue) => ({
        ...currentValue,
        status: 'failed',
        deliveryStatus: 'failed',
        retryable: true,
        deliveryErrorMessage: failureMessage,
      }))
      toast.error(failureMessage)
    } finally {
      setBackgroundSendCount((currentValue) => Math.max(currentValue - 1, 0))
    }
  }

  function handleRetryMessage(item) {
    const localMessageId = item?.id
    const retryPayload = localMessageId ? retryPayloadsRef.current[localMessageId] : null
    if (!localMessageId || !retryPayload) {
      toast.error('This message can no longer be retried.')
      return
    }

    updateLocalThreadMessage(localMessageId, (currentValue) => ({
      ...currentValue,
      status: 'sending',
      deliveryStatus: 'sending',
      retryable: false,
      deliveryErrorMessage: '',
    }))
    sendMessageInBackground(localMessageId, retryPayload)
  }

  function handleSendMessage() {
    const normalizedMessage = messageDraft.trim()
    const selectedFiles = [...attachmentFiles]
    if (!normalizedMessage && selectedFiles.length === 0) {
      toast.error('Enter a message or attach a file first.')
      return
    }

    const localMessageId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
    const optimisticMessage = buildOptimisticMessage({
      messageId: localMessageId,
      messageText: normalizedMessage,
      attachmentFiles: selectedFiles,
      targetDisplayName: threadState?.targetDisplayName || capabilities?.targetDisplayName || '',
    })

    retryPayloadsRef.current[localMessageId] = {
      message: normalizedMessage,
      files: selectedFiles,
    }

    applyThreadStateUpdate((currentValue) => ({
      ...currentValue,
      messages: mergeThreadMessages(currentValue?.messages || [], optimisticMessage),
    }))
    setMessageDraft('')
    resetAttachmentPicker()
    lastChatActivityRef.current = Date.now()
    sendMessageInBackground(localMessageId, retryPayloadsRef.current[localMessageId])
  }

  async function handlePrepareEmail() {
    setEmailLoading(true)
    const result = await prepareSupportAgentEmail(context)
    setEmailLoading(false)

    if (!result?.ok) {
      toast.error(result?.message || 'Unable to prepare the email draft.')
      return
    }

    const mailtoUri = result?.data?.mailtoUri
    if (!mailtoUri) {
      toast.error('Email draft URL was not returned.')
      return
    }
    window.location.href = mailtoUri
  }

  const channels = capabilities?.channels || {}
  const phoneChannel = channels?.phone || {}
  const whatsappChannel = channels?.whatsapp || {}
  const emailChannel = channels?.email || {}

  const messageSending = backgroundSendCount > 0
  const freeFormWindowLabel = useMemo(() => {
    const startLabel = formatThreadTimestamp(threadState?.freeFormWindowStartAt)
    const endLabel = formatThreadTimestamp(threadState?.freeFormWindowEndAt)
    if (!startLabel || !endLabel) {
      return ""
    }
    return startLabel + " to " + endLabel
  }, [threadState?.freeFormWindowStartAt, threadState?.freeFormWindowEndAt])
  const resolvedPhoneRoute = phoneRoutePreview?.route || null
  const routeProviderCandidates = Array.isArray(resolvedPhoneRoute?.providerCandidates)
    ? resolvedPhoneRoute.providerCandidates.filter(Boolean)
    : []
  const fallbackPhoneChannelCandidate =
    phoneChannel?.outcallNumber || phoneChannel?.providerType
      ? {
          providerType: phoneChannel?.providerType || '',
          providerLabel: phoneChannel?.providerLabel || '',
          callerNumbers: phoneChannel?.outcallNumber ? [phoneChannel.outcallNumber] : [],
          active: true,
          preferred: true,
        }
      : null
  const preferredPhoneRouteCandidate =
    routeProviderCandidates.find((candidate) => candidate?.preferred) ||
    routeProviderCandidates.find((candidate) => candidate?.active) ||
    fallbackPhoneChannelCandidate
  const activePhoneRouteCandidate =
    routeProviderCandidates.find((candidate) => candidate?.active) || null
  const secondaryPhoneRouteCandidate =
    activePhoneRouteCandidate &&
    preferredPhoneRouteCandidate &&
    activePhoneRouteCandidate?.providerType !== preferredPhoneRouteCandidate?.providerType
      ? activePhoneRouteCandidate
      : routeProviderCandidates.find(
          (candidate) =>
            candidate?.providerType &&
            candidate?.providerType !== preferredPhoneRouteCandidate?.providerType &&
            Array.isArray(candidate?.callerNumbers) &&
            candidate.callerNumbers.length > 0,
        ) || null
  const preferredPhoneRouteSummary = preferredPhoneRouteCandidate
    ? formatSupportRouteProvider(preferredPhoneRouteCandidate)
    : ''
  const secondaryPhoneRouteSummary = secondaryPhoneRouteCandidate
    ? formatSupportRouteProvider(secondaryPhoneRouteCandidate)
    : ''
  const waitingForPhoneRoutePreview =
    !!phoneChannel?.enabled &&
    !loading &&
    !requiresEventTypeSelection &&
    phoneRoutePreview?.loading &&
    !phoneRoutePreview?.loaded &&
    !resolvedPhoneRoute
  const targetPhone = threadState?.targetPhoneNumber || capabilities?.targetPhoneNumber || ''
  const targetWhatsapp = threadState?.targetWhatsappNumber || capabilities?.targetWhatsappNumber || ''
  const targetEmail = threadState?.targetEmail || capabilities?.targetEmail || ''
  const targetRoleLabel = targetRole || capabilities?.targetRole || 'contact'
  const hasDetails = Boolean(
    targetPhone
      || targetWhatsapp
      || targetEmail
      || threadState?.targetUserId
      || threadState?.targetActorId
      || threadState?.sharedWhatsappPatientIds?.length,
  )
  const detailsDrawerTitle = targetRoleLabel === 'patient' ? 'Patient details' : 'Contact details'

  const disableAllActions = loading || requiresEventTypeSelection
  const selectionMessage = requiresEventTypeSelection ? 'Choose an event category first.' : ''
  const canCall = !!phoneChannel?.enabled && !disableAllActions
  const canWhatsapp = !!whatsappChannel?.enabled && !disableAllActions
  const canEmail = !!emailChannel?.enabled && !disableAllActions
  const hasAnyVisibleAction = canCall || canWhatsapp || canEmail
  const composerDisabled = !threadState?.freeFormAllowed
  const sendDisabled = composerDisabled || (!messageDraft.trim() && attachmentFiles.length === 0)
  const attachmentMenuOpen = Boolean(attachmentMenuAnchorEl)

  if (
    (compact || compactIconStrip) &&
    !title &&
    !loading &&
    !capabilitiesError &&
    !selectionMessage &&
    !hasAnyVisibleAction
  ) {
    return null
  }

  // Lazy mode, not yet expanded: render a single trigger that loads contact capabilities on click,
  // instead of firing the capabilities/phone-route calls just because the drawer mounted.
  if (lazyUntilExpand && !expanded) {
    return (
      <Box onClick={(event) => event.stopPropagation()} sx={{ ...sx }}>
        <Button
          size="small"
          variant="outlined"
          onClick={() => setExpanded(true)}
          sx={{ textTransform: 'none' }}
        >
          {title || 'Contact'}
        </Button>
      </Box>
    )
  }

  return (
    <>
      {compactIconStrip ? (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.6,
            flexShrink: 0,
            ...sx,
          }}
          onClick={(event) => event.stopPropagation()}
        >
          {loading && !hasAnyVisibleAction ? (
            <CircularProgress size={16} sx={{ color: '#64748b', mx: 0.75 }} />
          ) : null}
          {canCall ? (
            <PortalVoiceCallButton
              actorRole="support-agent"
              patientId={patientId}
              practitionerId={practitionerId}
              bookedEventId={bookedEventId}
              clinicBrandId={clinicBrandId}
              clinicId={clinicId}
              targetRole={targetRole}
              eventType={resolvedEventType || undefined}
              eventTypesCsv={eventTypesCsv || undefined}
              uiSurface={uiSurface}
              allowedDestinationTypes={phoneChannel?.destinationType ? [phoneChannel.destinationType] : undefined}
              autoStartWhenSingleRoute
              buttonLabel="Call"
              iconOnly
              size="small"
              sx={{
                width: 30,
                height: 30,
                minWidth: 30,
                color: '#475569',
                background: 'rgba(248, 250, 252, 0.92)',
                border: '1px solid rgba(203, 213, 225, 0.9)',
                boxShadow: 'none',
                '&:hover': {
                  background: 'rgba(241, 245, 249, 0.96)',
                  boxShadow: 'none',
                },
              }}
            />
          ) : null}
          {canWhatsapp ? (
            <Tooltip title="WhatsApp" arrow enterDelay={650} enterNextDelay={650}>
              <span>
                <IconButton
                  size="small"
                  aria-label="WhatsApp"
                  onClick={openWhatsappDrawer}
                  sx={{
                    width: 30,
                    height: 30,
                    color: '#16a34a',
                    background: 'rgba(248, 250, 252, 0.92)',
                    border: '1px solid rgba(203, 213, 225, 0.9)',
                    '&:hover': {
                      background: 'rgba(240, 253, 244, 0.96)',
                    },
                  }}
                >
                  <WhatsAppIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
          {canEmail ? (
            <Tooltip title="Email" arrow enterDelay={650} enterNextDelay={650}>
              <span>
                <IconButton
                  size="small"
                  aria-label="Email"
                  onClick={handlePrepareEmail}
                  disabled={emailLoading}
                  sx={{
                    width: 30,
                    height: 30,
                    color: '#475569',
                    background: 'rgba(248, 250, 252, 0.92)',
                    border: '1px solid rgba(203, 213, 225, 0.9)',
                    '&:hover': {
                      background: 'rgba(241, 245, 249, 0.96)',
                    },
                  }}
                >
                  {emailLoading ? <CircularProgress size={14} color="inherit" /> : <EmailRoundedIcon sx={{ fontSize: 17 }} />}
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
        </Box>
      ) : (
        <Box
          sx={{
            p: compact ? 1.25 : 1.5,
            borderRadius: 2,
            border: '1px solid rgba(59, 130, 246, 0.14)',
            background:
              'linear-gradient(135deg, rgba(239, 246, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%)',
            ...sx,
          }}
        >
          {title ? (
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: '#1d4ed8', mb: compact ? 1 : 1.25 }}
            >
              {title}
            </Typography>
          ) : null}

          {!eventType && !eventTypesCsv ? (
            <TextField
              select
              fullWidth
              size="small"
              label="Support Category"
              value={selectedEventType}
              onChange={(event) => setSelectedEventType(event.target.value)}
              sx={{ mb: compact ? 1 : 1.25 }}
            >
              {normalizedEventTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          ) : null}

          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: compact ? 1 : 1.25 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                Checking allowed contact modes...
              </Typography>
            </Box>
          ) : null}

          {capabilitiesError ? (
            <Alert severity="warning" sx={{ mb: compact ? 1 : 1.25 }}>
              {capabilitiesError}
            </Alert>
          ) : null}

          {!capabilitiesError && selectionMessage ? (
            <Alert severity="info" sx={{ mb: compact ? 1 : 1.25 }}>
              {selectionMessage}
            </Alert>
          ) : null}

          {!loading && !capabilitiesError && !selectionMessage && !hasAnyVisibleAction ? (
            <Alert severity="info" sx={{ mb: compact ? 0 : 1.25 }}>
              No usable contact channel is currently available for this record.
            </Alert>
          ) : null}

          {hasAnyVisibleAction ? (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {canCall ? (
                <PortalVoiceCallButton
                  actorRole="support-agent"
                  patientId={patientId}
                  practitionerId={practitionerId}
                  bookedEventId={bookedEventId}
                  clinicBrandId={clinicBrandId}
                  clinicId={clinicId}
                  targetRole={targetRole}
                  eventType={resolvedEventType || undefined}
                  eventTypesCsv={eventTypesCsv || undefined}
                  uiSurface={uiSurface}
                  allowedDestinationTypes={phoneChannel?.destinationType ? [phoneChannel.destinationType] : undefined}
                  autoStartWhenSingleRoute
                  buttonLabel="Call"
                  size="small"
                />
              ) : null}

              {canWhatsapp ? (
                <Button
                  size="small"
                  variant="outlined"
                  color="success"
                  startIcon={<WhatsAppIcon />}
                  onClick={openWhatsappDrawer}
                >
                  WhatsApp
                </Button>
              ) : null}

              {canEmail ? (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={emailLoading ? <CircularProgress size={14} color="inherit" /> : <EmailRoundedIcon />}
                  onClick={handlePrepareEmail}
                  disabled={emailLoading}
                >
                  Email
                </Button>
              ) : null}
            </Stack>
          ) : null}

          {preferredPhoneRouteSummary && canCall && !waitingForPhoneRoutePreview ? (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                Preferred outcall route from this page: {preferredPhoneRouteSummary}.
              </Typography>
              {secondaryPhoneRouteSummary ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                  {activePhoneRouteCandidate &&
                  preferredPhoneRouteCandidate &&
                  activePhoneRouteCandidate?.providerType !== preferredPhoneRouteCandidate?.providerType
                    ? 'Current active calling route'
                    : 'Fallback route if the preferred provider is unavailable'}: {secondaryPhoneRouteSummary}.
                </Typography>
              ) : null}
              {resolvedPhoneRoute?.preferredProviderUnavailableReason &&
              activePhoneRouteCandidate &&
              preferredPhoneRouteCandidate &&
              activePhoneRouteCandidate?.providerType !== preferredPhoneRouteCandidate?.providerType ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                  The preferred provider is currently unavailable on the server, so live calls use the active route above.
                </Typography>
              ) : null}
            </Box>
          ) : null}
        </Box>
      )}

      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={whatsappDrawerOpen}
        onClose={() => {
          if (!threadLoading && !templateSending) {
            setWhatsappDrawerOpen(false)
          }
        }}
        PaperProps={{
          sx: {
            width: isMobile ? '100%' : 520,
            maxWidth: '100%',
            height: isMobile ? '86vh' : '100%',
            borderTopLeftRadius: isMobile ? 20 : 0,
            borderTopRightRadius: isMobile ? 20 : 0,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            background:
              'linear-gradient(180deg, rgba(248, 250, 252, 0.98) 0%, rgba(255, 255, 255, 1) 36%)',
          }}
        >
          <Box sx={{ px: 2, py: 1.25 }}>
            <Stack spacing={0.75}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.25}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                    WhatsApp
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {threadState?.targetDisplayName || capabilities?.targetDisplayName || 'Recipient'}
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                    {targetWhatsapp ? (
                      <Chip
                        size="small"
                        label={targetWhatsapp}
                        onClick={() => setDetailsOpen(true)}
                        sx={{ cursor: 'pointer' }}
                      />
                    ) : null}
                    {!targetWhatsapp && targetPhone ? (
                      <Chip
                        size="small"
                        label={targetPhone}
                        onClick={() => setDetailsOpen(true)}
                        sx={{ cursor: 'pointer' }}
                      />
                    ) : null}
                    {hasDetails ? (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => setDetailsOpen(true)}
                        sx={{ textTransform: 'none', minWidth: 0, px: 0.5 }}
                      >
                        Details
                      </Button>
                    ) : null}
                  </Stack>
                </Box>
                <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
                  {threadState?.templateActions?.length > 0 ? (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setShowTemplatePanel((value) => !value)}
                      sx={{ textTransform: 'none', minWidth: 0, px: 0.75 }}
                    >
                      {showTemplatePanel ? 'Hide' : 'Templates'}
                    </Button>
                  ) : null}
                  <IconButton
                    size="small"
                    onClick={() => loadWhatsappThread()}
                    disabled={threadLoading}
                    aria-label="Refresh"
                  >
                    <RefreshRoundedIcon fontSize="small" />
                  </IconButton>
                  <Button size="small" onClick={() => setWhatsappDrawerOpen(false)} sx={{ minWidth: 0, px: 0.75 }}>
                    Close
                  </Button>
                </Stack>
              </Stack>

              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
                {(capabilities?.resolvedEventTypes || []).map((item) => (
                  <Chip key={item} size="small" label={item} variant="outlined" />
                ))}
                {threadState?.freeFormAllowed ? (
                  <Chip size="small" color="success" label="Live" />
                ) : (
                  <Chip size="small" color="warning" label="Template needed" />
                )}
              </Stack>

              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.35 }}>
                {threadState?.freeFormAllowed
                  ? (threadState?.freeFormWindowEndAt
                    ? `Reply window till ${formatThreadTimestamp(threadState?.freeFormWindowEndAt)}`
                    : 'Reply window is open')
                  : (threadState?.freeFormWindowEndAt
                    ? `Reply window closed on ${formatThreadTimestamp(threadState?.freeFormWindowEndAt)}. Send an approved opener.`
                    : 'Send an approved opener to start chatting.')}
              </Typography>
            </Stack>
          </Box>

          <Divider />

          <Box
            sx={{
              flex: 1,
              px: 2.25,
              pb: 2,
              overflowY: 'auto',
            }}
          >
            {threadLoading ? (
              <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={28} />
              </Box>
            ) : threadError ? (
              <Alert severity="warning">{threadError}</Alert>
            ) : (
              <Stack spacing={1.25}>
                {threadState?.messages?.length > 0 ? (
                  <>
                    {threadState.messages.map((item) => (
                      <MessageBubble
                        key={item?.id || `${item?.createdAt}-${item?.actionType}`}
                        item={item}
                        onRetry={handleRetryMessage}
                      />
                    ))}
                    <Box ref={messagesEndRef} />
                  </>
                ) : (
                  <Box
                    sx={{
                      borderRadius: 3,
                      border: '1px dashed rgba(148, 163, 184, 0.4)',
                      background: 'rgba(248, 250, 252, 0.9)',
                      px: 2,
                      py: 3,
                      textAlign: 'center',
                    }}
                  >
                    <HistoryEduRoundedIcon sx={{ fontSize: 30, color: '#94a3b8', mb: 1 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                      No WhatsApp interactions yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {threadState?.freeFormAllowed
                        ? "Free-form window is already open for this number. You can send a message below."
                        : "Start with the approved template below so the conversation has a clean first message."}
                    </Typography>
                  </Box>
                )}
              </Stack>
            )}
          </Box>

          <Divider />

          <Box sx={{ px: 2.25, py: 2, display: 'grid', gap: 1.5 }}>
            {threadState?.templateActions?.length > 0 && showTemplatePanel ? (
              <Box
                sx={{
                  borderRadius: 2.5,
                  border: '1px solid rgba(34, 197, 94, 0.16)',
                  background: 'rgba(240, 253, 244, 0.8)',
                  px: 1.5,
                  py: 1.5,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#166534', mb: 1 }}>
                  Approved template
                </Typography>
                <Stack
                  direction={isMobile ? 'column' : 'row'}
                  spacing={1}
                  alignItems={isMobile ? 'stretch' : 'flex-end'}
                >
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Template action"
                    value={selectedTemplateAction}
                    onChange={(event) => setSelectedTemplateAction(event.target.value)}
                  >
                    {threadState.templateActions.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleSendTemplate}
                    disabled={!selectedTemplateAction || templateSending}
                    startIcon={templateSending ? <CircularProgress size={16} color="inherit" /> : <SendRoundedIcon />}
                    sx={{ minWidth: isMobile ? '100%' : 144 }}
                  >
                    Send template
                  </Button>
                </Stack>

                {selectedTemplateAction ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {threadState.templateActions.find((item) => item.id === selectedTemplateAction)?.description}
                  </Typography>
                ) : null}
              </Box>
            ) : !threadState?.freeFormAllowed ? (
              <Alert severity="warning">
                No approved WhatsApp template is configured for this contact yet, so outbound WhatsApp cannot be initiated from here.
              </Alert>
            ) : null}

            <Box
              sx={{
                display: 'grid',
                gap: 1,
              }}
            >
              {attachmentFiles.length > 0 ? (
                <Stack direction="row" spacing={0.75} sx={{ overflowX: 'auto', pb: 0.25 }}>
                  {attachmentFiles.map((file, index) => {
                    const attachmentMeta = describePendingAttachment(file)
                    return (
                      <Paper
                        key={`${file?.name || 'file'}-${index}`}
                        elevation={0}
                        sx={{
                          minWidth: 180,
                          maxWidth: 260,
                          px: 1,
                          py: 0.75,
                          borderRadius: 2.5,
                          border: '1px solid rgba(203, 213, 225, 0.9)',
                          background: '#ffffff',
                          flexShrink: 0,
                        }}
                      >
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: 1.5,
                              display: 'grid',
                              placeItems: 'center',
                              background: 'rgba(226, 232, 240, 0.9)',
                              color: '#0f172a',
                              flexShrink: 0,
                            }}
                          >
                            {getComposerAttachmentIcon(attachmentMeta.kind)}
                          </Box>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: '#0f172a' }} noWrap>
                              {file?.name || `Attachment ${index + 1}`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {attachmentMeta.secondaryLabel}
                            </Typography>
                          </Box>
                          <IconButton size="small" onClick={() => removeAttachmentAt(index)} sx={{ flexShrink: 0 }}>
                            <CloseRoundedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Stack>
                      </Paper>
                    )
                  })}
                </Stack>
              ) : null}

              {messageSending ? (
                <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>
                  {backgroundSendCount} message{backgroundSendCount === 1 ? '' : 's'} sending in background
                </Typography>
              ) : null}

              <Paper
                elevation={0}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: 0.5,
                  px: 0.75,
                  py: 0.5,
                  borderRadius: 3,
                  border: '1px solid rgba(203, 213, 225, 0.9)',
                  background: '#ffffff',
                  boxShadow: '0 10px 28px rgba(15, 23, 42, 0.06)',
                }}
              >
                <Tooltip title="Attach">
                  <span>
                    <IconButton
                      size="small"
                      onClick={openAttachmentMenu}
                      disabled={composerDisabled}
                      aria-label="Choose attachment type"
                      sx={{
                        width: 38,
                        height: 38,
                        color: '#54656f',
                      }}
                    >
                      <AttachFileRoundedIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </span>
                </Tooltip>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={6}
                    variant="standard"
                    value={messageDraft}
                    onChange={(event) => setMessageDraft(event.target.value)}
                    onKeyDown={handleComposerKeyDown}
                    placeholder={threadState?.freeFormAllowed ? 'Type a message' : 'Send an approved opener first'}
                    disabled={composerDisabled}
                    InputProps={{ disableUnderline: true }}
                    sx={{
                      '& .MuiInputBase-root': {
                        px: 0.5,
                        py: 0.75,
                        alignItems: 'flex-end',
                        fontSize: '0.95rem',
                        lineHeight: 1.5,
                      },
                      '& textarea': {
                        resize: 'none',
                      },
                    }}
                  />
                </Box>

                <Tooltip title={sendDisabled ? 'Type a message or attach a file' : (messageSending ? 'Send another message' : 'Send')}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleSendMessage}
                      disabled={sendDisabled}
                      aria-label="Send message"
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: sendDisabled ? 'rgba(148, 163, 184, 0.18)' : '#00a884',
                        color: sendDisabled ? '#94a3b8' : '#ffffff',
                        '&:hover': {
                          bgcolor: sendDisabled ? 'rgba(148, 163, 184, 0.24)' : '#019270',
                        },
                      }}
                    >
                      <SendRoundedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Paper>

              <input
                ref={attachmentInputRef}
                key={`${attachmentInputKey}-${attachmentPickerMode}`}
                hidden
                multiple
                type="file"
                accept={attachmentPickerAccept}
                onChange={handleAttachmentSelection}
              />
              <Menu
                anchorEl={attachmentMenuAnchorEl}
                open={attachmentMenuOpen}
                onClose={closeAttachmentMenu}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                PaperProps={{
                  sx: {
                    mt: -1,
                    borderRadius: 3,
                    minWidth: 250,
                    boxShadow: '0 20px 48px rgba(15, 23, 42, 0.18)',
                    border: '1px solid rgba(226, 232, 240, 0.9)',
                    p: 0.5,
                  },
                }}
              >
                {WHATSAPP_ATTACHMENT_OPTIONS.map((option) => (
                  <MenuItem key={option.id} onClick={() => triggerAttachmentPicker(option)} sx={{ borderRadius: 2, py: 1 }}>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 1.5,
                          display: 'grid',
                          placeItems: 'center',
                          background: 'rgba(240, 242, 245, 0.95)',
                          color: '#0f172a',
                          flexShrink: 0,
                        }}
                      >
                        {getComposerAttachmentIcon(option.kind, 20)}
                      </Box>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                          {option.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.helper}
                        </Typography>
                      </Box>
                    </Stack>
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          </Box>
        </Box>
      </Drawer>

      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        PaperProps={{
          sx: {
            width: isMobile ? '100%' : 360,
            maxWidth: '100%',
            borderTopLeftRadius: isMobile ? 20 : 0,
            borderTopRightRadius: isMobile ? 20 : 0,
            p: 2.25,
          },
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {detailsDrawerTitle}
            </Typography>
            <IconButton onClick={() => setDetailsOpen(false)}>
              <CloseRoundedIcon />
            </IconButton>
          </Stack>

          <Stack spacing={1}>
            <Typography variant="subtitle2" color="text.secondary">
              {threadState?.targetDisplayName || capabilities?.targetDisplayName || 'Recipient'}
            </Typography>
            <Typography variant="body2">Role: {targetRoleLabel}</Typography>
            {targetWhatsapp ? <Typography variant="body2">WhatsApp: {targetWhatsapp}</Typography> : null}
            {targetPhone ? <Typography variant="body2">Phone: {targetPhone}</Typography> : null}
            {targetEmail ? <Typography variant="body2">Email: {targetEmail}</Typography> : null}
            {threadState?.clinicBrandName ? (
              <Typography variant="body2">Clinic brand: {threadState.clinicBrandName}</Typography>
            ) : null}
            {bookedEventId ? <Typography variant="body2">Booked event: {bookedEventId}</Typography> : null}
            {threadState?.targetActorId ? <Typography variant="body2">Target ID: {threadState.targetActorId}</Typography> : null}
            {threadState?.targetUserId ? <Typography variant="body2">User ID: {threadState.targetUserId}</Typography> : null}
            {threadState?.sharedWhatsappPatientIds?.length > 1 ? (
              <Typography variant="body2">
                Linked patients: {threadState.sharedWhatsappPatientIds.join(', ')}
              </Typography>
            ) : null}
          </Stack>
        </Stack>
      </Drawer>
    </>
  )
}
